/**
 * Карта «Париж» для «Полёта птицы» — открытый мир на острове R = 1900.
 * Позиции — по реальным координатам (`geo(lat, lon)`): Шатле в центре, до
 * 4,5 км — 260 ед/км, дальше расстояние сжимается, чтобы Дефанс и леса
 * поместились на острове. Сена — реальная ломаная: с востока через острова
 * Сите и Сен-Луи, мимо Лувра и Конкорда, к Эйфелевой башне и петлёй на север
 * вдоль Булонского леса. Город — османовские кварталы со светлым камнем и
 * серыми цинковыми мансардами, развёрнутые по ближайшему проспекту; звезда
 * площади Этуаль. Достопримечательности: Эйфелева башня (ажур, ночью мерцает),
 * Триумфальная арка, Елисейские поля, Лувр с пирамидой, Тюильри, Конкорд с
 * обелиском и колесом, Нотр-Дам, Сакре-Кёр на Монмартре, Мулен Руж, Опера
 * Гарнье, Дом инвалидов, Пантеон, Помпиду, Бастилия, Люксембургский сад,
 * Трокадеро, Марсово поле, Монпарнас, Дефанс с Большой аркой, кораблики по
 * Сене. Всё из примитивов, мир детерминирован по seed.
 * Время суток: 0 — день, 0,5 — закат, 1 — ночь с огнями.
 */
/* eslint-disable */
import * as THREE from 'three';
import type { BirdFlightWorld } from './ekibastuzWorld.ts';
import {
  distanceCuller, facadeMaterial, instancedBySector, mergeStaticMeshes,
} from './worldOptimize.ts';

export function* buildParisWorld(scene: THREE.Scene): Generator<number, BirdFlightWorld, void> {
  // ─── Случайность по seed: одинаковый мир у всех игроков ───────────────────
  function mulberry32(a) {
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      let t = Math.imul(a ^ a >>> 15, 1 | a);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }
  const rnd = mulberry32(20261016);
  const rand = (a, b) => a + rnd() * (b - a);
  const pick = (list) => list[Math.floor(rnd() * list.length)];
  const smooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

  yield 0.05;
  // ─── Небо ────────────────────────────────────────────────────────────────
  const skyUniforms = {
    top: { value: new THREE.Color() }, mid: { value: new THREE.Color() }, horizon: { value: new THREE.Color() },
    sunDir: { value: new THREE.Vector3() }, sunColor: { value: new THREE.Color() }, sunStrength: { value: 1 },
  };
  const sky = new THREE.Mesh(new THREE.SphereGeometry(1400, 32, 16), new THREE.ShaderMaterial({
    side: THREE.BackSide, depthWrite: false, fog: false, uniforms: skyUniforms,
    vertexShader: 'varying vec3 vDir; void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
    fragmentShader: `
      uniform vec3 top, mid, horizon, sunColor, sunDir; uniform float sunStrength; varying vec3 vDir;
      void main(){
        float h = vDir.y;
        vec3 c = h > 0.18 ? mix(mid, top, smoothstep(0.18, 0.8, h)) : mix(horizon, mid, smoothstep(-0.02, 0.18, h));
        float d = max(dot(normalize(vDir), normalize(sunDir)), 0.0);
        c += sunColor * (pow(d, 2500.0) * 4.0 + pow(d, 90.0) * 0.35) * sunStrength;
        gl_FragColor = vec4(c, 1.0);
        #include <colorspace_fragment>
      }`,
  }));
  scene.add(sky);
  const nightStars = (() => {
    const pts = [];
    for (let i = 0; i < 1400; i++) {
      const u = rnd() * Math.PI * 2, v = Math.acos(rand(0.05, 1));
      pts.push(Math.sin(v) * Math.cos(u) * 1350, Math.cos(v) * 1350, Math.sin(v) * Math.sin(u) * 1350);
    }
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    const p = new THREE.Points(g, new THREE.PointsMaterial({ color: 0xffffff, size: 2.2, sizeAttenuation: false, transparent: true, fog: false }));
    scene.add(p); return p;
  })();

  const FOG_NEAR_DAY = 1150, FOG_NEAR_NIGHT = 900;
  const fog = new THREE.Fog(0xffffff, FOG_NEAR_DAY, 1380);
  scene.fog = fog;
  const hemi = new THREE.HemisphereLight(0xffffff, 0x3a3048, 1);
  scene.add(hemi);
  const sun = new THREE.DirectionalLight(0xffffff, 1);
  scene.add(sun);

  const lambert = (color, extra = {}) => new THREE.MeshLambertMaterial({ color, flatShading: true, ...extra });
  const box = new THREE.BoxGeometry(1, 1, 1);
  const lights = [];
  const redLights = [];
  function lightMat(dayColor, nightColor) {
    const m = new THREE.MeshBasicMaterial({ color: dayColor });
    lights.push({ m, day: new THREE.Color(dayColor), night: new THREE.Color(nightColor) });
    return m;
  }
  const SECTOR_SIZE = 1300;
  function instanced(geo, material, list) {
    if (list.length) instancedBySector(scene, geo, material, list, SECTOR_SIZE);
  }
  function add(geo, material, x, y, z, sx = 1, sy = 1, sz = 1, ry = 0) {
    const m = new THREE.Mesh(geo, material);
    m.position.set(x, y, z); m.scale.set(sx, sy, sz); m.rotation.y = ry; scene.add(m); return m;
  }
  // Проём-арка (плоская фигура в плоскости XY, смотрит в +z): стрельчатая
  // готическая или полукруглая. Ставится на фасад чуть впереди стены.
  function archGeo(w, h, pointed = true) {
    const sh = new THREE.Shape(), r = w / 2, hs = h - (pointed ? w * 0.85 : r);
    sh.moveTo(-r, 0); sh.lineTo(-r, hs);
    if (pointed) { sh.quadraticCurveTo(-r, h - w * 0.2, 0, h); sh.quadraticCurveTo(r, h - w * 0.2, r, hs); }
    else sh.absarc(0, hs, r, Math.PI, 0, true);
    sh.lineTo(r, 0); sh.closePath();
    return new THREE.ShapeGeometry(sh, 6);
  }
  // Крыша-призма: треугольник w × h, вытянутый на длину len вдоль локального z.
  function prismGeo(w, h, len) {
    const sh = new THREE.Shape(); sh.moveTo(-w / 2, 0); sh.lineTo(w / 2, 0); sh.lineTo(0, h); sh.closePath();
    const g = new THREE.ExtrudeGeometry(sh, { depth: len, bevelEnabled: false }); g.translate(0, 0, -len / 2);
    return g;
  }
  // Точка в локальной системе объекта: u — вправо, v — вперёд (по ry).
  const loc = (c, ry, u, v) => ({ x: c.x + u * Math.cos(ry) + v * Math.sin(ry), z: c.z - u * Math.sin(ry) + v * Math.cos(ry) });

  yield 0.08;
  // ─── Координаты ──────────────────────────────────────────────────────────
  // (0,0) — Шатле, x < 0 — восток, z > 0 — север (как в Алматы и Барселоне).
  const ISLAND = 1900;
  const ORIGIN = { lat: 48.8580, lon: 2.3470 };
  const KM_LAT = 111.2, KM_LON = 73.2, SCALE = 260, NEAR_KM = 4.5;
  function geo(lat, lon) {
    const n = (lat - ORIGIN.lat) * KM_LAT, e = (lon - ORIGIN.lon) * KM_LON, d = Math.hypot(n, e);
    const r = d <= NEAR_KM ? d * SCALE : NEAR_KM * SCALE + 520 * (1 - Math.exp(-(d - NEAR_KM) / 3));
    const k = d ? r / d : 0;
    return { x: -e * k, z: n * k };
  }
  const P = {
    eiffel: geo(48.8584, 2.2945),
    arc: geo(48.8738, 2.2950),
    concorde: geo(48.8656, 2.3212),
    louvre: geo(48.8611, 2.3358),
    notredame: geo(48.8530, 2.3499),
    sacre: geo(48.8867, 2.3431),
    moulin: geo(48.8841, 2.3322),
    opera: geo(48.8720, 2.3316),
    invalides: geo(48.8550, 2.3125),
    pantheon: geo(48.8462, 2.3464),
    pompidou: geo(48.8606, 2.3522),
    bastille: geo(48.8532, 2.3692),
    montparnasse: geo(48.8421, 2.3219),
    luxembourg: geo(48.8462, 2.3372),
    trocadero: geo(48.8620, 2.2880),
    ecole: geo(48.8510, 2.3060),
    grandpalais: geo(48.8661, 2.3125),
    defense: geo(48.8924, 2.2360),
    boulogne: geo(48.8620, 2.2490),
    vincennes: geo(48.8350, 2.4330),
    tuileries: geo(48.8635, 2.3275),
  };

  yield 0.10;
  // ─── Сена, острова, рельеф ───────────────────────────────────────────────
  const SEINE = [
    [48.812, 2.440], [48.825, 2.405], [48.836, 2.381], [48.846, 2.366], [48.8515, 2.3590], [48.8545, 2.3500],
    [48.8575, 2.3410], [48.8605, 2.3300], [48.8637, 2.3200], [48.8640, 2.3110], [48.8630, 2.3000], [48.8600, 2.2910],
    [48.8545, 2.2830], [48.8460, 2.2740], [48.8380, 2.2680], [48.8290, 2.2620], [48.8270, 2.2500], [48.8320, 2.2360],
    [48.8430, 2.2280], [48.8580, 2.2290], [48.8720, 2.2350], [48.8840, 2.2470], [48.8950, 2.2620], [48.9050, 2.2830],
    [48.9110, 2.3050], [48.9140, 2.3350],
  ].map(([la, lo]) => geo(la, lo));
  const RIVER_HALF = 17;
  // Острова Сите и Сен-Луи — суша посреди расширенного русла.
  const CITE = [[48.8572, 2.3395], [48.8560, 2.3445], [48.8540, 2.3520], [48.8522, 2.3525], [48.8530, 2.3460], [48.8555, 2.3405]].map(([la, lo]) => geo(la, lo));
  const STLOUIS = [[48.8530, 2.3540], [48.8520, 2.3600], [48.8505, 2.3605], [48.8512, 2.3545]].map(([la, lo]) => geo(la, lo));
  const ISLANDS_C = geo(48.8535, 2.3500);
  function inPoly(poly, x, z) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const a = poly[i], b = poly[j];
      if ((a.z > z) !== (b.z > z) && x < (b.x - a.x) * (z - a.z) / (b.z - a.z) + a.x) inside = !inside;
    }
    return inside;
  }
  const segDist = (x, z, a, b) => {
    const dx = b.x - a.x, dz = b.z - a.z, t = Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / (dx * dx + dz * dz)));
    return { d: Math.hypot(x - a.x - dx * t, z - a.z - dz * t), t };
  };
  function riverDist(x, z) {
    let best = Infinity;
    for (let i = 0; i < SEINE.length - 1; i++) best = Math.min(best, segDist(x, z, SEINE[i], SEINE[i + 1]).d);
    return best;
  }
  // У островов русло шире — два рукава вокруг суши.
  const riverHalf = (x, z) => RIVER_HALF + 26 * Math.exp(-((x - ISLANDS_C.x) ** 2 + (z - ISLANDS_C.z) ** 2) / (2 * 70 * 70));
  const onIsle = (x, z) => inPoly(CITE, x, z) || inPoly(STLOUIS, x, z);
  const inRiver = (x, z, m = 0) => riverDist(x, z) < riverHalf(x, z) + m && !onIsle(x, z);

  const gauss = (x, z, c, r) => Math.exp(-((x - c.x) ** 2 + (z - c.z) ** 2) / (2 * r * r));
  const BELLEVILLE = geo(48.8720, 2.3880), VALERIEN = geo(48.8710, 2.2120), CHAILLOT = geo(48.8640, 2.2860), STGENEVIEVE = geo(48.8462, 2.3470);
  function rawHeight(x, z) {
    const rd = riverDist(x, z), half = riverHalf(x, z);
    let h = onIsle(x, z) ? 1.2 : THREE.MathUtils.lerp(-4, 1.2, smooth(half - 6, half + 3, rd));
    // Холмы: Монмартр, Бельвиль, Мон-Валерьен за Дефанс, Шайо, гора Святой Женевьевы.
    h += 62 * gauss(x, z, P.sacre, 85) + 38 * gauss(x, z, BELLEVILLE, 150) + 70 * gauss(x, z, VALERIEN, 120);
    h += 10 * gauss(x, z, CHAILLOT, 70) + 9 * gauss(x, z, STGENEVIEVE, 70);
    // Площадка под Сакре-Кёр.
    const sd = Math.hypot(x - P.sacre.x, z - P.sacre.z);
    if (sd < 40) h = THREE.MathUtils.lerp(63.2, h, smooth(22, 40, sd));
    h += smooth(ISLAND * 0.85, ISLAND * 1.15, Math.hypot(x, z)) * 45;
    return h;
  }
  const height = (x, z) => Math.max(0, rawHeight(x, z));

  {
    const seg = 220;
    const grid = new THREE.PlaneGeometry(ISLAND * 2.6, ISLAND * 2.6, seg, seg);
    grid.rotateX(-Math.PI / 2);
    const gridPos = grid.attributes.position;
    for (let i = 0; i < gridPos.count; i++) gridPos.setY(i, rawHeight(gridPos.getX(i), gridPos.getZ(i)));
    const geom = grid.toNonIndexed();
    grid.dispose();
    const pos = geom.attributes.position;
    const city = [0xb9b2a6, 0xb2ab9f, 0xbfb8ac].map((c) => new THREE.Color(c));
    const grass = [0x7fa65a, 0x76a053, 0x86ad62].map((c) => new THREE.Color(c));
    const quay = new THREE.Color(0xc9bda4), bed = new THREE.Color(0x3d5f6a);
    const colors = [];
    for (let f = 0; f < pos.count; f += 3) {
      const x = (pos.getX(f) + pos.getX(f + 1) + pos.getX(f + 2)) / 3, z = (pos.getZ(f) + pos.getZ(f + 1) + pos.getZ(f + 2)) / 3;
      const y = (pos.getY(f) + pos.getY(f + 1) + pos.getY(f + 2)) / 3;
      const r = Math.hypot(x, z);
      const c = y < 0 ? bed : riverDist(x, z) < riverHalf(x, z) + 9 ? quay : r > ISLAND * 0.8 || y > 25 ? pick(grass) : pick(city);
      for (let k = 0; k < 3; k++) colors.push(c.r, c.g, c.b);
    }
    geom.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geom.computeVertexNormals();
    scene.add(new THREE.Mesh(geom, new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true })));
  }
  // Вода: одна плоскость под всей сушей — видна только в русле.
  const water = add(new THREE.PlaneGeometry(ISLAND * 2.6, ISLAND * 2.6), new THREE.MeshPhongMaterial({ color: 0x4d7f8f, specular: 0x3a5560, shininess: 25 }), 0, 0.4, 0);
  water.rotation.x = -Math.PI / 2;

  yield 0.16;
  // ─── Проспекты ───────────────────────────────────────────────────────────
  // Звезда Этуаль: 12 проспектов от арки; Елисейские поля к Конкорду; Риволи;
  // Севастопольский бульвар; Сен-Жермен; бульвар Османа; Большие бульвары.
  const AVENUES = [];
  const avenue = (a, b, w) => AVENUES.push({ a, b, w });
  // Площадь Звезды: кольцо проезжей части вокруг арки, проспекты — от её края.
  const ETOILE_R = 44;
  const OPERA_PLACE = geo(48.8706, 2.3323);
  for (let k = 0; k < 12; k++) {
    const ang = Math.atan2(P.concorde.x - P.arc.x, P.concorde.z - P.arc.z) + k / 12 * Math.PI * 2;
    const len = k === 0 ? Math.hypot(P.concorde.x - P.arc.x, P.concorde.z - P.arc.z) : 420;
    // Проспект начинается у края площади (ETOILE_R), а не в центре арки.
    avenue({ x: P.arc.x + Math.sin(ang) * ETOILE_R, z: P.arc.z + Math.cos(ang) * ETOILE_R }, { x: P.arc.x + Math.sin(ang) * len, z: P.arc.z + Math.cos(ang) * len }, k === 0 ? 16 : 10);
  }
  // Риволи идёт вдоль северного крыла Лувра, а не сквозь дворы.
  avenue(geo(48.8662, 2.3228), geo(48.8645, 2.3315), 10);
  avenue(geo(48.8645, 2.3315), geo(48.8612, 2.3435), 10);
  avenue(geo(48.8612, 2.3435), geo(48.8558, 2.3620), 10);       // → Сент-Антуан
  avenue(geo(48.8800, 2.3550), geo(48.8460, 2.3440), 10);        // Севастополь → Сен-Мишель
  avenue(geo(48.8590, 2.3170), geo(48.8500, 2.3560), 10);        // Сен-Жермен
  avenue(geo(48.8755, 2.3080), geo(48.8730, 2.3460), 10);        // Осман — за Оперой, не сквозь неё
  // Большие бульвары: от Мадлен через площадь Оперы к Республике.
  avenue(geo(48.8700, 2.3245), geo(48.8706, 2.3323), 10);
  avenue(geo(48.8706, 2.3323), geo(48.8700, 2.3480), 10);
  avenue(geo(48.8700, 2.3480), geo(48.8675, 2.3640), 10);
  avenue(geo(48.8706, 2.3323), geo(48.8640, 2.3352), 9);          // авеню Оперы — от площади перед фасадом до Риволи
  const etoileEdge = (to) => { const dx = to.x - P.arc.x, dz = to.z - P.arc.z, l = Math.hypot(dx, dz); return { x: P.arc.x + dx / l * ETOILE_R, z: P.arc.z + dz / l * ETOILE_R }; };
  avenue(P.trocadero, etoileEdge(P.trocadero), 9);                 // Клебер — до края площади Звезды
  avenue(etoileEdge(P.defense), P.defense, 14);                    // ось к Дефанс — от края площади
  avenue(P.invalides, P.grandpalais, 10);                          // эспланада + мост Александра III
  avenue(P.montparnasse, P.luxembourg, 9);
  avenue(P.bastille, geo(48.8480, 2.3960), 10);                    // к Нации
  const avenueDist = (x, z) => {
    let best = { d: Infinity, ang: 0, w: 0 };
    AVENUES.forEach((av) => { const { d } = segDist(x, z, av.a, av.b); if (d < best.d) best = { d, ang: Math.atan2(av.b.x - av.a.x, av.b.z - av.a.z), w: av.w }; });
    return best;
  };

  // Участки достопримечательностей и парки: кварталов там нет.
  const LOTS = [
    [P.eiffel, 45], [P.arc, 42], [P.concorde, 40], [P.louvre, 70], [P.notredame, 30], [P.sacre, 45], [P.moulin, 12],
    [P.opera, 34], [P.invalides, 70], [P.pantheon, 24], [P.pompidou, 22], [P.bastille, 22], [P.montparnasse, 22],
    [P.luxembourg, 62], [P.trocadero, 45], [P.grandpalais, 32], [P.defense, 0], [P.tuileries, 50], [P.ecole, 26],
  ].map(([c, r]: any) => ({ x: c.x, z: c.z, r }));
  // Марсово поле — полоса от башни к Военной школе.
  const champ = (x, z) => segDist(x, z, P.eiffel, P.ecole).d < 34;
  const tuil = (x, z) => segDist(x, z, P.concorde, P.louvre).d < 30;
  const champsEl = (x, z) => segDist(x, z, P.arc, P.concorde).d < 14;
  // Лувр — прямоугольник вдоль оси Тюильри: кварталы туда не ставим.
  const LOUVRE_RY = Math.atan2(P.louvre.x - P.concorde.x, P.louvre.z - P.concorde.z);
  // Центр дворца сдвинут от Сены на север: иначе Большая галерея стояла в воде.
  const LOUVRE_C = loc(P.louvre, LOUVRE_RY, 14, 4);
  const inLouvre = (x, z, m = 0) => {
    const dx = x - LOUVRE_C.x, dz = z - LOUVRE_C.z;
    const u = dx * Math.cos(LOUVRE_RY) - dz * Math.sin(LOUVRE_RY), v = dx * Math.sin(LOUVRE_RY) + dz * Math.cos(LOUVRE_RY);
    return Math.abs(u) < 44 + m && v > -102 - m && v < 62 + m;
  };
  const inLot = (x, z, m = 0) => LOTS.some((l) => Math.hypot(x - l.x, z - l.z) < l.r + m) || champ(x, z) || tuil(x, z) || inLouvre(x, z, m);
  const inWood = (x, z) => ((x - P.boulogne.x) / 150) ** 2 + ((z - P.boulogne.z) / 230) ** 2 < 1 || ((x - P.vincennes.x) / 190) ** 2 + ((z - P.vincennes.z) / 130) ** 2 < 1;

  yield 0.20;
  // ─── Османовские кварталы ────────────────────────────────────────────────
  // Квартал — прямоугольное кольцо с двором (одна геометрия на все), сверху
  // серая цинковая мансарда; ориентация — по ближайшему проспекту. Высота
  // почти одинаковая, как и в настоящем Париже (6–7 этажей).
  const trunks = [], crowns = [], lampPoles = [], lampHeads = [];
  const facadeGlow = { value: 0 };
  const facade = (color) => facadeMaterial(color, facadeGlow);
  const stoneMats = [facade(0xe9e0cc), facade(0xe0d5bd), facade(0xefe8d8)];
  const ringOf = (hole) => {
    const sh = new THREE.Shape([[-0.5, -0.5], [0.5, -0.5], [0.5, 0.5], [-0.5, 0.5]].map(([u, v]) => new THREE.Vector2(u, v)));
    sh.holes.push(new THREE.Path([[-hole, -hole], [-hole, hole], [hole, hole], [hole, -hole]].map(([u, v]) => new THREE.Vector2(u, v))));
    const g = new THREE.ExtrudeGeometry(sh, { depth: 1, bevelEnabled: false });
    g.rotateX(-Math.PI / 2); g.translate(0, -0.5, 0);
    return g;
  };
  const ringGeo = ringOf(0.22);
  // Мансарда: кольцо пирамидальное — наружные стены скошены внутрь.
  const mansardGeo = (() => {
    const g = new THREE.BoxGeometry(1, 1, 1);
    const p = g.attributes.position;
    for (let i = 0; i < p.count; i++) if (p.getY(i) > 0) { p.setX(i, p.getX(i) * 0.86); p.setZ(i, p.getZ(i) * 0.86); }
    g.computeVertexNormals();
    return g;
  })();
  const blocks = new Map(), mansards = [];
  // Пятна крупных построек (x, z, радиус): деревья, посаженные раньше, там убираются.
  const BLOCKERS = [];
  function footprintFree(x, z, w, d, ang) {
    const c = Math.cos(ang), sn = Math.sin(ang);
    for (const [fu, fv] of [[-0.5, -0.5], [0.5, -0.5], [0.5, 0.5], [-0.5, 0.5], [0, -0.5], [0, 0.5], [-0.5, 0], [0.5, 0]]) {
      const u = fu * w, v = fv * d, px = x + u * c + v * sn, pz = z - u * sn + v * c;
      if (inRiver(px, pz, 6) || rawHeight(px, pz) < 1 || inLot(px, pz, 2) || champsEl(px, pz)) return false;
      const av = avenueDist(px, pz);
      if (av.d < av.w / 2 + 2) return false;
    }
    return true;
  }
  const houses = new Map(), roofsTile = [];
  function addHouse(mat, it) { if (!houses.has(mat)) houses.set(mat, []); houses.get(mat).push(it); }
  {
    const CELL = 40;
    for (let gx = -ISLAND; gx < ISLAND; gx += CELL) {
      for (let gz = -ISLAND; gz < ISLAND; gz += CELL) {
        const x = gx + rand(-4, 4), z = gz + rand(-4, 4), r = Math.hypot(x, z);
        if (r > ISLAND * 0.82) continue;
        const h0 = rawHeight(x, z);
        if (h0 < 1 || inRiver(x, z, 16) || inLot(x, z, 14) || inWood(x, z) || champsEl(x, z)) continue;
        if (Math.hypot(x - P.defense.x, z - P.defense.z) < 150) continue;      // Дефанс — свои башни
        const av = avenueDist(x, z);
        if (av.d < av.w / 2 + 12) continue;
        // За Периферик (≈ 1200) — пригороды реже и ниже.
        const outer = r > 1250;
        if (outer && rnd() < 0.6) continue;
        if (h0 > 50 && rnd() < 0.5) continue;
        const ang = av.ang + (rnd() < 0.5 ? 0 : Math.PI / 2);
        const w = rand(29, 35), d = rand(26, 33), hh = outer ? rand(10, 16) : rand(17, 21);
        // Квартал большой и повёрнутый: проверяем всё пятно (углы и середины
        // сторон), а не только центр — иначе углы залезали в Сену и на проспекты.
        if (!footprintFree(x, z, w, d, ang)) continue;
        const mat = pick(stoneMats);
        if (!blocks.has(mat)) blocks.set(mat, []);
        blocks.get(mat).push({ x, y: h0 + hh / 2, z, sx: w, sy: hh, sz: d, ry: ang });
        mansards.push({ x, y: h0 + hh + 2.4, z, sx: w, sy: 4.8, sz: d, ry: ang });
        // Платаны вдоль проспектов.
      }
    }
  }
  function tree(x, z) {
    if (inRiver(x, z, 3) || rawHeight(x, z) < 1) return;
    const y = height(x, z), h = rand(7, 10);
    trunks.push({ x, y: y + h * 0.35, z, sx: 0.45, sy: h * 0.7, sz: 0.45 });
    crowns.push({ x, y: y + h * 0.8, z, sx: rand(2.6, 3.6), sy: h * 0.45, sz: rand(2.6, 3.6), ry: rnd() * 3 });
  }
  // Деревья вдоль проспектов — рядами по обе стороны.
  AVENUES.forEach((av) => {
    const dx = av.b.x - av.a.x, dz = av.b.z - av.a.z, len = Math.hypot(dx, dz), nx = dz / len, nz = -dx / len;
    for (let s = 6; s < len; s += 10) [-1, 1].forEach((sd) => {
      const x = av.a.x + dx * s / len + nx * sd * (av.w / 2 + 2), z = av.a.z + dz * s / len + nz * sd * (av.w / 2 + 2);
      if (Math.hypot(x - P.arc.x, z - P.arc.z) < ETOILE_R + 4) return;   // на площади Звезды — пусто
      if (Math.hypot(x - OPERA_PLACE.x, z - OPERA_PLACE.z) < 24) return;   // и на площади Оперы
      if (!inLot(x, z, 4) && !inRiver(x, z, 6)) tree(x, z);
      if (s % 30 < 10) { const y = height(x, z); lampPoles.push({ x: x + nx * sd * 2, y: y + 3.5, z: z + nz * sd * 2, sx: 0.25, sy: 7, sz: 0.25 }); lampHeads.push({ x: x + nx * sd * 2, y: y + 7.2, z: z + nz * sd * 2, sx: 0.8, sy: 0.6, sz: 0.8 }); }
    });
  });
  // Набережные: деревья вдоль Сены.
  for (let i = 0; i < SEINE.length - 1; i++) {
    const a = SEINE[i], b = SEINE[i + 1], len = Math.hypot(b.x - a.x, b.z - a.z), nx = (b.z - a.z) / len, nz = -(b.x - a.x) / len;
    for (let s = 0; s < len; s += 11) [-1, 1].forEach((sd) => {
      const x = a.x + (b.x - a.x) * s / len + nx * sd * (riverHalf(a.x, a.z) + 7), z = a.z + (b.z - a.z) * s / len + nz * sd * (riverHalf(a.x, a.z) + 7);
      if (Math.hypot(x, z) < ISLAND * 0.85 && !inLot(x, z)) tree(x, z);
    });
  }

  yield 0.30;
  // ─── Мосты через Сену и кораблики ────────────────────────────────────────
  const boats = [];
  {
    const stone = lambert(0xcfc3a8);
    const bridgeAt = (lat, lon) => {
      const c = geo(lat, lon);
      let best = 0, bd = Infinity;
      for (let i = 0; i < SEINE.length - 1; i++) { const d = segDist(c.x, c.z, SEINE[i], SEINE[i + 1]).d; if (d < bd) { bd = d; best = i; } }
      const a = SEINE[best], b = SEINE[best + 1], ry = Math.atan2(b.x - a.x, b.z - a.z) + Math.PI / 2;
      add(box, stone, c.x, 3, c.z, 12, 1.6, riverHalf(c.x, c.z) * 2 + 16, ry);
      [-0.5, 0, 0.5].forEach((t) => { const p = loc(c, ry, 0, t * riverHalf(c.x, c.z) * 1.4); add(box, stone, p.x, 1, p.z, 10, 3, 3, ry); });
    };
    [[48.8638, 2.3136], [48.8637, 2.3200], [48.8600, 2.3290], [48.8571, 2.3412], [48.8580, 2.3330], [48.8610, 2.2930],
      [48.8554, 2.2876], [48.8637, 2.3016], [48.8506, 2.3600], [48.8435, 2.3666], [48.8545, 2.3470], [48.8470, 2.2760]].forEach(([la, lo]) => bridgeAt(la, lo));
    // Кораблики-«бато-муш» — едут по Сене туда и обратно.
    for (let k = 0; k < 8; k++) {
      const g = new THREE.Group();
      const hull = new THREE.Mesh(box, lambert(0xf2f2ee)); hull.scale.set(4, 1.4, 16); hull.position.y = 1.1; g.add(hull);
      const cab = new THREE.Mesh(box, lightMat(0x6f8fa8, 0xffd890)); cab.scale.set(3.4, 1.6, 11); cab.position.y = 2.6; g.add(cab);
      scene.add(g);
      boats.push({ g, t: rnd(), dir: k % 2 ? 1 : -1, side: k % 2 ? 6 : -6 });
    }
  }
  const SEINE_LEN = SEINE.slice(1).reduce((s, p, i) => s + Math.hypot(p.x - SEINE[i].x, p.z - SEINE[i].z), 0);
  function seinePoint(t) {
    let d = t * SEINE_LEN;
    for (let i = 0; i < SEINE.length - 1; i++) {
      const a = SEINE[i], b = SEINE[i + 1], l = Math.hypot(b.x - a.x, b.z - a.z);
      if (d <= l) return { x: a.x + (b.x - a.x) * d / l, z: a.z + (b.z - a.z) * d / l, ang: Math.atan2(b.x - a.x, b.z - a.z) };
      d -= l;
    }
    const b = SEINE[SEINE.length - 1]; return { x: b.x, z: b.z, ang: 0 };
  }

  yield 0.34;
  // ─── Эйфелева башня ──────────────────────────────────────────────────────
  // Ажур — текстура с прозрачностью (alphaTest): решётка «крестами» на
  // гранях. Четыре опоры с арками, две площадки, сужающийся ствол, шпиль.
  // Ночью башня светится золотом и мерцает огоньками.
  const eiffelMat = (() => {
    const cv = document.createElement('canvas'); cv.width = cv.height = 64;
    const g = cv.getContext('2d');
    g.strokeStyle = '#fff'; g.lineWidth = 5;
    g.strokeRect(2, 2, 60, 60);
    g.lineWidth = 3; g.beginPath(); g.moveTo(0, 0); g.lineTo(64, 64); g.moveTo(64, 0); g.lineTo(0, 64); g.stroke();
    const tex = new THREE.CanvasTexture(cv); tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    // Без mip-уровней: усреднённая решётка вдали становилась сплошной стеной.
    tex.generateMipmaps = false; tex.minFilter = THREE.LinearFilter;
    tex.colorSpace = THREE.SRGBColorSpace;
    return new THREE.MeshLambertMaterial({ color: 0x8a6a4a, map: tex, alphaTest: 0.5, side: THREE.DoubleSide, emissive: 0xffb050, emissiveIntensity: 0 });
  })();
  const sparkles = (() => {
    const pts = [];
    const { x, z } = P.eiffel;
    for (let i = 0; i < 260; i++) {
      const y = rand(2, 160), hw = y < 30 ? 22 - y * 0.35 : y < 62 ? 12 - (y - 30) * 0.15 : Math.max(1, 7 - (y - 62) * 0.06);
      const side = Math.floor(rnd() * 4), u = rand(-hw, hw);
      const [px, pz] = side === 0 ? [u, hw] : side === 1 ? [u, -hw] : side === 2 ? [hw, u] : [-hw, u];
      pts.push(x + px, y, z + pz);
    }
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    const p = new THREE.Points(g, new THREE.PointsMaterial({ color: 0xffffff, size: 3, sizeAttenuation: false, transparent: true, opacity: 0 }));
    scene.add(p); return p;
  })();
  {
    const { x, z } = P.eiffel, y0 = height(x, z), ry = Math.atan2(P.ecole.x - x, P.ecole.z - z);
    // Сужающаяся квадратная «труба» с текстурой решётки.
    const frustum = (cx, cz, yA, yB, hwA, hwB, tilt = null) => {
      const g = new THREE.CylinderGeometry(hwB * Math.SQRT2, hwA * Math.SQRT2, yB - yA, 4, 1, true);
      g.rotateY(Math.PI / 4);
      const uv = g.attributes.uv;
      for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * 4 * Math.max(1, Math.round(hwA / 2.5)), uv.getY(i) * Math.max(1, Math.round((yB - yA) / 5)));
      const m = add(g, eiffelMat, cx, y0 + (yA + yB) / 2, cz, 1, 1, 1, ry);
      if (tilt) { m.rotation.set(tilt.rx, ry, tilt.rz, 'YXZ'); }
      return m;
    };
    // Опоры: от углов основания (полуширина 26) к углам первой площадки (14).
    [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(([su, sv]) => {
      const base = loc(P.eiffel, ry, su * 22, sv * 22), top = loc(P.eiffel, ry, su * 12, sv * 12);
      const g = new THREE.CylinderGeometry(3, 5.2, 1, 4, 1, true); g.rotateY(Math.PI / 4);
      const uv = g.attributes.uv; for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * 4, uv.getY(i) * 5);
      const len = Math.hypot(top.x - base.x, 30, top.z - base.z);
      const leg = new THREE.Mesh(g, eiffelMat);
      leg.position.set((base.x + top.x) / 2, y0 + 15, (base.z + top.z) / 2);
      leg.scale.set(1, len, 1);
      leg.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(top.x - base.x, 30, top.z - base.z).normalize());
      scene.add(leg);
      add(box, lambert(0x8a7a64), base.x, y0 + 1, base.z, 9, 2, 9, ry);
    });
    // Арки между опорами.
    for (let k = 0; k < 4; k++) {
      const a = ry + k * Math.PI / 2, c = loc(P.eiffel, a, 0, 17);
      const arc = add(new THREE.TorusGeometry(14, 0.7, 4, 16, Math.PI), lambert(0x7a5a3e), c.x, y0 + 12, c.z);
      arc.rotation.set(0, a, 0);
    }
    // Первая площадка, второй ярус, вторая площадка, ствол, верх.
    add(box, lambert(0x7a5a3e), x, y0 + 30, z, 30, 3, 30, ry);
    frustum(x, z, 31.5, 60, 12.5, 6.5);
    add(box, lambert(0x7a5a3e), x, y0 + 61, z, 15, 2.4, 15, ry);
    // Ствол — изогнутый профиль: собран из трёх частей.
    frustum(x, z, 62, 100, 5.6, 2.8);
    frustum(x, z, 100, 140, 2.8, 1.4);
    frustum(x, z, 140, 156, 1.4, 0.9);
    add(box, lambert(0x7a5a3e), x, y0 + 157, z, 2.6, 2.4, 2.6, ry);
    add(new THREE.CylinderGeometry(0.3, 0.6, 10, 6), lambert(0x9a9a9a), x, y0 + 164, z);
    const beacon = add(new THREE.SphereGeometry(1, 8, 6), lightMat(0x8a6a4a, 0xfff2c0), x, y0 + 158, z);
    redLights.push(beacon);
    // Марсово поле: газон, дорожки, Военная школа в конце.
    const e = P.ecole, mid = { x: (x + e.x) / 2, z: (z + e.z) / 2 }, len = Math.hypot(e.x - x, e.z - z);
    add(box, lambert(0x7fae5a), mid.x, 1.3, mid.z, 52, 0.3, len - 40, ry);
    [-1, 1].forEach((sd) => { const p = loc(mid, ry, sd * 8, 0); add(box, lambert(0xd8cfba), p.x, 1.45, p.z, 4, 0.2, len - 50, ry); });
    for (let s = 30; s < len - 20; s += 8) [-24, 24].forEach((u) => { const p = loc(P.eiffel, ry, u, s); tree(p.x, p.z); });
    add(box, lambert(0xe2d8c2), e.x, 8, e.z, 70, 14, 22, ry);
    add(new THREE.SphereGeometry(7, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2), lambert(0x6f7a86), e.x, 15, e.z);
  }

  yield 0.40;
  // ─── Трокадеро: дворец Шайо с крыльями-дугами и фонтаны к башне ─────────
  {
    const t = P.trocadero, ty = height(t.x, t.z), ry = Math.atan2(P.eiffel.x - t.x, P.eiffel.z - t.z);
    const pale = lambert(0xece4d2);
    for (let k = -1; k <= 1; k += 2) {
      for (let i = 0; i < 8; i++) {
        const a = k * (0.25 + i * 0.12), p = loc(t, ry, Math.sin(a) * 55, -Math.cos(a) * 30 + 22);
        add(box, pale, p.x, ty + 7, p.z, 16, 14, 12, ry + a);
      }
    }
    for (let i = 0; i < 4; i++) { const p = loc(t, ry, 0, 30 + i * 9); add(box, new THREE.MeshPhongMaterial({ color: 0x6fb8d8, shininess: 80 }), p.x, height(p.x, p.z) + 0.6, p.z, 30 - i * 3, 0.4, 7, ry); }
  }

  yield 0.43;
  // ─── Триумфальная арка и Елисейские поля ─────────────────────────────────
  {
    const a = P.arc, y0 = height(a.x, a.z), ry = Math.atan2(P.concorde.x - a.x, P.concorde.z - a.z);
    // Площадь Звезды: асфальтовое кольцо и светлый «остров» с аркой.
    add(new THREE.CylinderGeometry(ETOILE_R, ETOILE_R, 0.4, 48), lambert(0x5a5c62), a.x, y0 + 1.3, a.z);
    add(new THREE.CylinderGeometry(24, 24, 0.6, 40), lambert(0xd8d0c0), a.x, y0 + 1.45, a.z);
    // Арка по фото: почти куб из светлого камня. Большой пролёт сквозь
    // длинные фасады (по оси Елисейских полей), малые — сквозь боковые; на
    // опорах горельефы, выше — панели-рельефы; фриз, тяжёлый карниз, аттик
    // со щитами. Размеры: ширина 30, глубина 15, высота 34.
    const st = lambert(0xece6d8, { emissive: 0x3a3630 }), stD = lambert(0xd2c8b2, { emissive: 0x2a2620 });
    const relief = lambert(0xc4b89e), aHole = lambert(0x3a3a40, { side: THREE.DoubleSide });
    const aat = (u, v, yy, geom, mat, sx = 1, sy = 1, sz = 1, rot = 0) => { const p = loc(a, ry, u, v); return add(geom, mat, p.x, y0 + yy, p.z, sx, sy, sz, ry + rot); };
    const W = 30, D = 15, PW = 7.5;                          // ширина, глубина, ширина опоры
    // Опоры и верхний массив над пролётом.
    [-1, 1].forEach((su) => aat(su * (W / 2 - PW / 2), 0, 12, box, st, PW, 24, D));
    aat(0, 0, 28.5, box, st, W, 9, D);
    // Свод большого пролёта (полуцилиндр) и тёмные «щёки» под ним.
    // Ось свода — вдоль пролёта (локальный v); верхняя половина цилиндра.
    aat(0, 0, 18, new THREE.CylinderGeometry(PW, PW, D, 18, 1, true, Math.PI / 2, Math.PI), stD).rotation.set(Math.PI / 2, ry, 0, 'YXZ');
    [-1, 1].forEach((sv) => {
      aat(0, sv * (D / 2 + 0.05), 18, new THREE.RingGeometry(PW, PW + 1, 18, 1, 0, Math.PI), stD, 1, 1, 1, sv > 0 ? 0 : Math.PI);
      // Горельефы на опорах и рельефные панели над ними.
      [-1, 1].forEach((su) => {
        aat(su * (W / 2 - PW / 2), sv * (D / 2 + 0.3), 9, box, relief, 4.6, 8, 0.6);
        aat(su * (W / 2 - PW / 2), sv * (D / 2 + 0.2), 20.5, box, relief, 5.4, 3, 0.4);
      });
    });
    // Малые пролёты сквозь боковые фасады.
    [-1, 1].forEach((su) => aat(su * (W / 2 + 0.05), 0, 2.5, archGeo(5, 12, false), aHole, 1, 1, 1, su * Math.PI / 2));
    [-1, 1].forEach((su) => aat(su * (W / 2 - PW - 0.05), 0, 2.5, archGeo(5, 12, false), aHole, 1, 1, 1, -su * Math.PI / 2));
    // Фриз, карниз, аттик со щитами.
    aat(0, 0, 26.5, box, relief, W + 0.4, 1.6, D + 0.4);
    aat(0, 0, 28.6, box, stD, W + 1.6, 1, D + 1.6);
    aat(0, 0, 31.8, box, st, W - 0.6, 4.6, D - 0.6);
    aat(0, 0, 34.3, box, stD, W, 0.6, D);
    for (let k = -6; k <= 6; k++) [-1, 1].forEach((sv) => aat(k * 2.1, sv * (D / 2 - 0.25), 31.8, new THREE.CircleGeometry(0.7, 10), relief, 1, 1, 1, sv > 0 ? 0 : Math.PI));
    // Флаг под аркой.
    [[0x2d4fa0, -1.4], [0xf4f4f4, 0], [0xd8352a, 1.4]].forEach(([c, u]) => { const p = loc(a, ry, u, 0); add(box, lambert(c), p.x, y0 + 14, p.z, 1.4, 9, 0.1, ry); });
    // Елисейские поля — широкий проспект с двумя рядами деревьев.
    const c = P.concorde, len = Math.hypot(c.x - a.x, c.z - a.z), mid = { x: (a.x + c.x) / 2, z: (a.z + c.z) / 2 };
    add(box, lambert(0x5a5c62), mid.x, 1.3, mid.z, 16, 0.3, len - 70, ry);
    // Остальные проспекты — полотно дорог.
    AVENUES.slice(1).forEach((av) => { const dx = av.b.x - av.a.x, dz = av.b.z - av.a.z, l = Math.hypot(dx, dz); add(box, lambert(0x5a5c62), (av.a.x + av.b.x) / 2, height((av.a.x + av.b.x) / 2, (av.a.z + av.b.z) / 2) + 1.3, (av.a.z + av.b.z) / 2, av.w, 0.3, l, Math.atan2(dx, dz)); });
  }

  yield 0.47;
  // ─── Конкорд, Тюильри, Лувр, Гран-Пале ───────────────────────────────────
  const wheel = { mesh: null as THREE.Object3D | null };
  {
    const c = P.concorde, ry = Math.atan2(P.louvre.x - c.x, P.louvre.z - c.z);
    add(box, lambert(0xd8d0c0), c.x, 1.3, c.z, 70, 0.3, 50, ry);
    // Обелиск с золотой верхушкой и два фонтана.
    add(box, lambert(0xc9a882), c.x, 13, c.z, 2.6, 24, 2.6, ry);
    add(new THREE.ConeGeometry(1.9, 3, 4), new THREE.MeshLambertMaterial({ color: 0xe0b040, emissive: 0x3a2800 }), c.x, 26.5, c.z).rotation.y = ry + Math.PI / 4;
    [-1, 1].forEach((sd) => { const p = loc(c, ry, sd * 0, sd * 16); add(new THREE.CylinderGeometry(6, 6, 1.2, 16), new THREE.MeshPhongMaterial({ color: 0x6fb8d8, shininess: 80 }), p.x, 1.8, p.z); add(new THREE.CylinderGeometry(1, 2.5, 5, 8), lambert(0x3a5a4a), p.x, 4, p.z); });
    // Колесо обозрения в Тюильри.
    const wp = loc(c, ry, 22, 24);
    const w = new THREE.Group(); w.position.set(wp.x, 30, wp.z); w.rotation.y = ry; scene.add(w);
    w.add(new THREE.Mesh(new THREE.TorusGeometry(24, 0.6, 6, 40), lightMat(0xdedede, 0xffe8a0)));
    for (let k = 0; k < 14; k++) {
      const a = k / 14 * Math.PI * 2;
      const sp = new THREE.Mesh(box, lightMat(0xbdbdbd, 0xffd040)); sp.scale.set(0.3, 48, 0.3); sp.rotation.z = a; w.add(sp);
      const cab = new THREE.Mesh(box, lambert(0xf2f2f2)); cab.position.set(Math.cos(a) * 24, Math.sin(a) * 24, 0); cab.scale.set(2.4, 2.4, 2.4); w.add(cab);
    }
    wheel.mesh = w;
    [-1, 1].forEach((k) => { const leg = add(box, lambert(0xcfcfcf), wp.x, 15, wp.z, 0.9, 32, 0.9, ry); leg.rotation.set(0, ry, k * 0.4, 'YXZ'); });
    // Тюильри: газон, ряды деревьев, круглые бассейны.
    const l = P.louvre, len = Math.hypot(l.x - c.x, l.z - c.z), mid = loc(c, ry, 0, len * 0.38);
    add(box, lambert(0x8fb065), mid.x, 1.35, mid.z, 58, 0.3, len * 0.48, ry);
    add(box, lambert(0xd8cfba), mid.x, 1.5, mid.z, 8, 0.2, len * 0.48, ry);
    for (let s = 40; s < len * 0.6; s += 7) [-26, -18, 18, 26].forEach((u) => { const p = loc(c, ry, u, s); tree(p.x, p.z); });
    [0.22, 0.55].forEach((t) => { const p = loc(c, ry, 0, len * t); add(new THREE.CylinderGeometry(7, 7, 0.6, 20), new THREE.MeshPhongMaterial({ color: 0x6fb8d8, shininess: 80 }), p.x, 1.6, p.z); });
    // Лувр, как на аэрофото: на востоке закрытый квадратный двор Кур-Каре,
    // западнее — двор Наполеона с пирамидой в «П» крыльев Ришелье и Денон,
    // дальше длинные галереи вдоль Сены (юг) и Риволи (север) до павильонов
    // Флоры и Марсан у Тюильри. Светлый камень, серые мансарды, павильоны —
    // с высокими тёмными куполами-«шапками»; розовая арка Карусель.
    // Локально: v — на восток (к Кур-Каре), u — на север (к Риволи).
    const l2 = LOUVRE_C;
    const pal = facade(0xeadfc6), roofG = lambert(0x6c7680), dark = lambert(0x4f5862);
    const wingL = (u1, v1, u2, v2, h = 13) => {
      const a = loc(l2, ry, u1, v1), b = loc(l2, ry, u2, v2), len2 = Math.hypot(b.x - a.x, b.z - a.z), cx = (a.x + b.x) / 2, cz = (a.z + b.z) / 2;
      const ang = Math.atan2(b.x - a.x, b.z - a.z);
      addHouse(pal, { x: cx, y: 1.2 + h / 2, z: cz, sx: 7, sy: h, sz: len2 + 7, ry: ang });
      add(prismGeo(7.4, 4, len2 + 7), roofG, cx, 1.2 + h, cz, 1, 1, 1, ang);
    };
    const pavilion = (u, v, w, h, roofH, mat = dark) => {
      const p = loc(l2, ry, u, v);
      addHouse(pal, { x: p.x, y: 1.2 + h / 2, z: p.z, sx: w, sy: h, sz: w, ry });
      add(new THREE.CylinderGeometry(w * 0.32, w * 0.62, roofH, 4), mat, p.x, 1.2 + h + roofH / 2, p.z, 1, 1, 1, ry + Math.PI / 4);
    };
    // Кур-Каре: квадрат 42 × 42.
    wingL(-21, 14, -21, 56); wingL(21, 14, 21, 56); wingL(-21, 56, 21, 56); wingL(-21, 14, 21, 14);
    pavilion(0, 14, 11, 16, 9);                           // павильон Часов (Сюлли)
    add(box, lambert(0xcfc4ae), loc(l2, ry, 0, 35).x, 1.35, loc(l2, ry, 0, 35).z, 34, 0.3, 34, ry);
    // Двор Наполеона: крылья Ришелье (север) и Денон (юг) с павильонами.
    wingL(-24, -22, -24, 14); wingL(24, -22, 24, 14);
    pavilion(-24, -2, 10, 15, 8); pavilion(24, -2, 10, 15, 8);
    pavilion(-24, -22, 9, 14, 7); pavilion(24, -22, 9, 14, 7);
    // Длинные галереи к Тюильри: вдоль Риволи и Большая галерея вдоль Сены.
    wingL(-24, -22, -38, -22); wingL(24, -22, 38, -22);
    wingL(-38, -22, -38, -96, 12); wingL(38, -22, 38, 14, 12); wingL(38, -22, 38, -96, 12);
    pavilion(-38, -96, 12, 15, 9); pavilion(38, -96, 12, 15, 9);   // Марсан и Флора
    // Пирамида и малые пирамидки, бассейны вокруг.
    const pyr = loc(l2, ry, 0, 0);
    add(box, lambert(0xd8cfba), pyr.x, 1.35, pyr.z, 46, 0.3, 36, ry);
    add(new THREE.ConeGeometry(8, 11, 4), new THREE.MeshPhongMaterial({ color: 0xa8cbe0, specular: 0xffffff, shininess: 120, transparent: true, opacity: 0.85, flatShading: true }), pyr.x, 7, pyr.z).rotation.y = ry + Math.PI / 4;
    [[0, 16], [-12, 0], [12, 0]].forEach(([u, v]) => { const p = loc(l2, ry, u, v); add(new THREE.ConeGeometry(2.2, 3, 4), lambert(0xa8cbe0), p.x, 2.9, p.z).rotation.y = ry + Math.PI / 4; });
    [[-10, 10], [10, 10], [-10, -9], [10, -9]].forEach(([u, v]) => { const p = loc(l2, ry, u, v); add(box, new THREE.MeshPhongMaterial({ color: 0x5f9fbf, shininess: 80 }), p.x, 1.6, p.z, 8, 0.3, 6, ry); });
    // Арка Карусель: розовый мрамор, квадрига наверху.
    const car = loc(l2, ry, 0, -40);
    add(box, lambert(0xd9a8a0), car.x, 1.2 + 6, car.z, 7, 12, 18, ry);
    add(box, new THREE.MeshLambertMaterial({ color: 0xd9a93a, emissive: 0x3a2800 }), car.x, 14.2, car.z, 3, 2.6, 5, ry);
    // Сад Карусель между пирамидой и Тюильри.
    const cg = loc(l2, ry, 0, -70); add(box, lambert(0x8fb065), cg.x, 1.35, cg.z, 60, 0.3, 50, ry);
    for (let v = -90; v < -50; v += 7) [-24, -14, 14, 24].forEach((u) => { const p = loc(l2, ry, u, v); tree(p.x, p.z); });
    // Гран-Пале: стеклянный свод.
    const g = P.grandpalais;
    add(box, pal, g.x, 7, g.z, 34, 14, 50, ry);
    add(new THREE.CylinderGeometry(15, 15, 46, 16, 1, false, 0, Math.PI), new THREE.MeshPhongMaterial({ color: 0x8fb0c4, specular: 0xffffff, shininess: 90, flatShading: true }), g.x, 14, g.z).rotation.set(Math.PI / 2, 0, ry + Math.PI / 2, 'YXZ');
  }

  yield 0.52;
  // ─── Нотр-Дам на острове Сите ────────────────────────────────────────────
  // По фото: западный фасад в три яруса — три стрельчатых портала, галерея
  // королей, роза между двумя высокими окнами, открытая колоннада, сверху
  // две плоские квадратные башни с парами высоких проёмов. Сзади — неф под
  // крутой серой крышей, боковые нефы, трансепт с розами, аркбутаны, тонкий
  // шпиль над перекрестьем, круглая апсида. Фасад — на запад.
  {
    const n = P.notredame, y0 = height(n.x, n.z);
    const ry = Math.atan2(STLOUIS[0].x - n.x, STLOUIS[0].z - n.z) + Math.PI;
    const stone = lambert(0xd6c8a6, { emissive: 0x2a2416 }), stoneD = lambert(0xbcae8e), lead = lambert(0x6c7178), hole = lambert(0x33343a, { side: THREE.DoubleSide });
    const at = (u, v, yy, geom, mat, sx = 1, sy = 1, sz = 1, rot = 0) => { const p = loc(n, ry, u, v); return add(geom, mat, p.x, y0 + yy, p.z, sx, sy, sz, ry + rot); };
    const F = 26;                                            // плоскость фасада (v)
    at(0, F - 3, 13, box, stone, 24, 26, 6);
    [-1, 1].forEach((sd) => at(sd * 8.25, F - 3, 31, box, stone, 7.5, 10, 6));
    [[-7.5, 4.4, 8], [0, 5.4, 9.5], [7.5, 4.4, 8]].forEach(([u, w, h]) => at(u, F + 0.05, 0.4, archGeo(w, h), hole));
    at(0, F + 0.1, 10.2, box, stoneD, 24.4, 0.6, 0.5);
    for (let u = -11; u <= 11; u += 1.25) at(u, F + 0.08, 11.6, archGeo(0.8, 2, false), hole);
    at(0, F + 0.1, 13.2, box, stoneD, 24.4, 0.6, 0.5);
    at(0, F + 0.06, 18, new THREE.CircleGeometry(3.8, 24), hole);
    at(0, F + 0.1, 18, new THREE.RingGeometry(1.2, 3.2, 24, 1), lightMat(0x5a6a8a, 0xffc070));
    [-1, 1].forEach((sd) => [-1.1, 1.1].forEach((o) => at(sd * 8.25 + o, F + 0.06, 14.5, archGeo(1.6, 6.5), hole)));
    at(0, F + 0.04, 24, box, hole, 23.6, 3.6, 0.2);
    for (let u = -11.5; u <= 11.5; u += 0.9) at(u, F + 0.2, 24, box, stone, 0.25, 3.6, 0.25);
    at(0, F + 0.1, 26.2, box, stoneD, 24.4, 0.8, 0.6);
    [-1, 1].forEach((sd) => {
      const cu = sd * 8.25;
      [-1.6, 1.6].forEach((o) => {
        at(cu + o, F + 0.06, 27.4, archGeo(1.9, 7), hole);
        const side = loc(n, ry, cu + sd * 3.8, F - 3 + o);
        add(archGeo(1.9, 7), hole, side.x, y0 + 27.4, side.z, 1, 1, 1, ry + sd * Math.PI / 2);
      });
      at(cu, F - 3, 36.3, box, stoneD, 7.9, 0.6, 6.4);
    });
    // Неф с крутой свинцовой крышей, боковые нефы.
    at(0, -2, 10, box, stone, 12, 20, 46);
    at(0, -2, 20, prismGeo(12.6, 8, 46), lead);
    [-1, 1].forEach((sd) => { at(sd * 9, -2, 6, box, stone, 6, 12, 46); at(sd * 9, -2, 12.2, box, lead, 6.2, 0.6, 46); });
    // Трансепт с розами на торцах.
    at(0, 4, 10, box, stone, 30, 20, 7);
    at(0, 4, 20, prismGeo(7.4, 6, 30), lead, 1, 1, 1, Math.PI / 2);
    [-1, 1].forEach((sd) => { const p = loc(n, ry, sd * 15.05, 4); add(new THREE.CircleGeometry(2.8, 20), hole, p.x, y0 + 14, p.z, 1, 1, 1, ry + sd * Math.PI / 2); });
    // Апсида: полукруг с конической крышей.
    at(0, -25, 10, new THREE.CylinderGeometry(6, 6, 20, 14, 1, false, Math.PI / 2, Math.PI), stone);
    at(0, -25, 23, new THREE.ConeGeometry(6.3, 6, 14, 1, false, Math.PI / 2, Math.PI), lead);
    at(0, -25, 6, new THREE.CylinderGeometry(10, 10, 12, 14, 1, false, Math.PI / 2, Math.PI), stone);
    // Аркбутаны: опора с пинаклем снаружи и наклонная арка к стене нефа.
    const fly = [], piers = [], pins = [];
    const fb = (u, v) => {
      const out = loc(n, ry, u, v), inn = loc(n, ry, u * 0.5, v);
      piers.push({ x: out.x, y: y0 + 8, z: out.z, sx: 1.4, sy: 16, sz: 1.4, ry });
      pins.push({ x: out.x, y: y0 + 18, z: out.z, sx: 1, sy: 4, sz: 1, ry });
      fly.push({ x: (out.x + inn.x) / 2, y: y0 + 15.5, z: (out.z + inn.z) / 2, sx: 0.7, sy: 0.9, sz: Math.abs(u) * 0.62, q: new THREE.Quaternion().setFromEuler(new THREE.Euler(0.45, Math.atan2(inn.x - out.x, inn.z - out.z), 0, 'YXZ')) });
    };
    for (let v = -20; v <= 18; v += 6) [-1, 1].forEach((sd) => fb(sd * 13.5, v));
    instanced(box, stone, piers); instanced(box, stone, fly);
    instanced(new THREE.ConeGeometry(0.8, 1, 4), stoneD, pins);
    // Шпиль над перекрестьем.
    at(0, 4, 27, new THREE.CylinderGeometry(1.6, 1.8, 3, 8), lead);
    at(0, 4, 38, new THREE.ConeGeometry(1.4, 20, 8), lead);
    at(0, 4, 48.5, box, lambert(0xd9a93a), 0.2, 1.6, 0.2);
    // Площадь перед собором и сквер за апсидой.
    const sq = loc(n, ry, 0, F + 18); add(box, lambert(0xd8cfba), sq.x, 1.35, sq.z, 30, 0.3, 26, ry);
    for (let k = 0; k < 30; k++) { const p = loc(n, ry, rand(-14, 14), rand(-50, -38)); tree(p.x, p.z); }
  }
  // Застройка островов — низкие дома с мансардами.
  {
    const isle = [...CITE, ...STLOUIS];
    const xs = isle.map((p) => p.x), zs = isle.map((p) => p.z);
    for (let k = 0; k < 220; k++) {
      const x = rand(Math.min(...xs), Math.max(...xs)), z = rand(Math.min(...zs), Math.max(...zs));
      if (!onIsle(x, z) || Math.hypot(x - P.notredame.x, z - P.notredame.z) < 55) continue;
      if (rawHeight(x, z) < 1) continue;
      const mat = pick(stoneMats);
      if (!houses.has(mat)) houses.set(mat, []);
      const h = rand(8, 11);
      houses.get(mat).push({ x, y: 1.2 + h / 2, z, sx: 8, sy: h, sz: 10, ry: rnd() * 3 });
      roofsTile.push({ x, y: 1.2 + h + 1.6, z, sx: 7, sy: 3.2, sz: 9, ry: 0 });
    }
  }

  yield 0.56;
  // ─── Монмартр: Сакре-Кёр и Мулен Руж ─────────────────────────────────────
  const millBlades = { mesh: null as THREE.Object3D | null };
  {
    // Сакре-Кёр: белый романо-византийский храм. Главный купол вытянутый,
    // на высоком барабане с аркадой окон, с рёбрами и фонарём; малые купола
    // по углам; спереди портик из трёх арок, над ним щипец с нишей и статуей
    // Христа, по бокам портика — конные статуи; сзади высокая квадратная
    // колокольня с куполом; полукруглая апсида с куполками.
    const s = P.sacre, y0 = height(s.x, s.z), ry = Math.atan2(-s.x, -s.z); // фасад к центру города
    const white = lambert(0xf6f3ec, { emissive: 0x6a6862 }), whiteD = lambert(0xe2ddd2, { emissive: 0x4a4844 });
    const sHole = lambert(0x3a3a40, { side: THREE.DoubleSide }), bronze = lambert(0x4f6a5a);
    const at = (u, v, yy, geom, mat = white, sx = 1, sy = 1, sz = 1, rot = 0) => { const p = loc(s, ry, u, v); return add(geom, mat, p.x, y0 + yy, p.z, sx, sy, sz, ry + rot); };
    const dome = (u, v, yy, r, h, drumH) => {
      at(u, v, yy + drumH / 2, new THREE.CylinderGeometry(r * 0.9, r * 0.95, drumH, 16));
      for (let k = 0; k < 10; k++) { const a = k / 10 * Math.PI * 2; const p = loc(s, ry, u + Math.cos(a) * r * 0.91, v + Math.sin(a) * r * 0.91); add(archGeo(r * 0.22, drumH * 0.6, false), sHole, p.x, y0 + yy + drumH * 0.2, p.z, 1, 1, 1, ry - a + Math.PI / 2); }
      at(u, v, yy + drumH, new THREE.SphereGeometry(r, 18, 12, 0, Math.PI * 2, 0, Math.PI / 2), white, 1, h / r, 1);
      at(u, v, yy + drumH + h + r * 0.15, new THREE.CylinderGeometry(r * 0.22, r * 0.26, r * 0.5, 8));
      at(u, v, yy + drumH + h + r * 0.55, new THREE.ConeGeometry(r * 0.24, r * 0.45, 8));
      at(u, v, yy + drumH + h + r * 0.85, box, whiteD, 0.15, r * 0.35, 0.15);
    };
    // Корпус: неф, трансепт, полукруглая апсида.
    at(0, 0, 10, box, white, 22, 20, 34);
    at(0, 2, 9, box, white, 36, 18, 14);
    at(0, -17, 9, new THREE.CylinderGeometry(11, 11, 18, 18, 1, false, Math.PI / 2, Math.PI));
    // Главный купол, четыре малых, куполки апсиды.
    dome(0, 0, 20, 7.5, 13, 9);
    [[-11, 8], [11, 8], [-11, -6], [11, -6]].forEach(([u, v]) => dome(u, v, 18, 3, 5, 4));
    [[-7, -22], [7, -22]].forEach(([u, v]) => dome(u, v, 17, 2.2, 3.4, 3));
    // Портик: три арки, щипец с нишей и Христом, конные статуи.
    const PF = 20;
    at(0, PF - 2, 7, box, white, 20, 14, 6);
    [-6, 0, 6].forEach((u) => at(u, PF + 1.05, 0.5, archGeo(4, 9, false), sHole));
    at(0, PF - 2, 15, prismGeo(18, 6, 5), white, 1, 1, 1, Math.PI / 2);
    at(0, PF + 0.6, 14.5, archGeo(2.8, 5, false), sHole);
    at(0, PF + 0.4, 16, new THREE.CylinderGeometry(0.6, 0.9, 3.2, 8), white);
    [-1, 1].forEach((sd) => { at(sd * 12.5, PF + 1, 9, box, white, 3, 6, 3); at(sd * 12.5, PF + 1, 13.5, box, bronze, 1.4, 3, 3.4); at(sd * 12.5, PF + 1, 16, new THREE.SphereGeometry(0.6, 8, 6), bronze); });
    // Колокольня сзади.
    at(0, -28, 22, box, white, 8, 44, 8);
    [-1, 1].forEach((sd) => { at(sd * 4.05, -28, 36, archGeo(2.4, 6, false), sHole, 1, 1, 1, sd * Math.PI / 2); });
    at(0, -24, 36, archGeo(2.4, 6, false), sHole);
    dome(0, -28, 44, 3.6, 5, 3);
    // Парадная лестница вниз по склону, сквер Луизы Мишель.
    for (let k = 0; k < 10; k++) { const p = loc(s, ry, 0, 30 + k * 7); add(box, lambert(0xd8d0c0), p.x, height(p.x, p.z) + 0.4, p.z, 30, 0.8, 7, ry); }
    // Деревья сквера — не на лестнице (|u| < 18, v 26–104) и не у колокольни сзади (v < -12).
    for (let k = 0; k < 140; k++) {
      const r = rand(30, 90), a = rnd() * Math.PI * 2;
      const u = Math.cos(a) * r, v = Math.sin(a) * r;
      if ((Math.abs(u) < 18 && v > 26 && v < 104) || (Math.abs(u) < 20 && v < -12 && v > -44)) continue;
      const p = loc(s, ry, u, v);
      tree(p.x, p.z);
    }
    // Мулен Руж: красный фасад, мельница с крутящимися крыльями.
    const m = P.moulin, my = height(m.x, m.z), mr = Math.atan2(-m.x, -m.z);
    add(box, lightMat(0xb8262a, 0xff3a3a), m.x, my + 5, m.z, 20, 10, 10, mr);
    const mp = loc(m, mr, -4, 0);
    add(new THREE.CylinderGeometry(3, 3.6, 12, 8), lightMat(0xc8302f, 0xff4a4a), mp.x, my + 14, mp.z);
    add(new THREE.ConeGeometry(4, 4, 8), lambert(0x7a2a24), mp.x, my + 22, mp.z);
    const hub = new THREE.Group(); const hp = loc(m, mr, -4, 3.8); hub.position.set(hp.x, my + 18, hp.z); hub.rotation.y = mr; scene.add(hub);
    const blades = new THREE.Group(); hub.add(blades);
    for (let k = 0; k < 4; k++) { const b = new THREE.Mesh(box, lightMat(0xd8c8a8, 0xffe070)); b.scale.set(1.8, 11, 0.3); b.position.set(Math.sin(k * Math.PI / 2) * 5.5, Math.cos(k * Math.PI / 2) * 5.5, 0); b.rotation.z = -k * Math.PI / 2; blades.add(b); }
    millBlades.mesh = blades;
  }

  yield 0.60;
  // ─── Опера Гарнье, Дом инвалидов, Пантеон, Помпиду, Бастилия, Монпарнас ─
  {
    const gold = new THREE.MeshLambertMaterial({ color: 0xe0b040, emissive: 0x3a2800 });
    // Опера Гарнье, как на фото: внизу аркада из семи арок, над ней лоджия с
    // парными колоннами, аттик; по краям — золотые скульптурные группы и
    // угловые павильоны с куполками. За фасадом — низкий зелёный купол зала с
    // золотой короной и высокий объём сцены с треугольным фронтоном и
    // золотым Аполлоном на вершине. Боковые круглые павильоны.
    const o = P.opera, oy = height(o.x, o.z), ory = Math.atan2(P.louvre.x - o.x, P.louvre.z - o.z);
    const cream = lambert(0xe2d4b2, { emissive: 0x2a2416 }), creamD = lambert(0xc9b994), copper = lambert(0x5f9a80), oHole = lambert(0x3a3630, { side: THREE.DoubleSide });
    const oat = (u, v, yy, geom, mat, sx = 1, sy = 1, sz = 1, rot = 0) => { const p = loc(o, ory, u, v); return add(geom, mat, p.x, oy + yy, p.z, sx, sy, sz, ory + rot); };
    const OF = 22;                                           // плоскость фасада
    oat(0, -2, 9, box, cream, 34, 18, 44);
    oat(0, OF - 3, 8.5, box, cream, 32, 17, 6);
    for (let k = -3; k <= 3; k++) oat(k * 4.2, OF + 0.05, 0.5, archGeo(2.8, 5.6, false), oHole);
    oat(0, OF + 0.05, 7.6, box, creamD, 32.4, 0.6, 0.4);
    oat(0, OF + 0.04, 11.4, box, oHole, 26, 6.6, 0.2);
    const pairs = [];
    for (let k = -3; k <= 3; k++) [-0.55, 0.55].forEach((d) => { const p = loc(o, ory, k * 4.2 + d, OF + 0.6); pairs.push({ x: p.x, y: oy + 11.4, z: p.z, sx: 0.55, sy: 6.6, sz: 0.55 }); });
    instanced(new THREE.CylinderGeometry(1, 1, 1, 8), lambert(0xf0e6cc), pairs);
    oat(0, OF - 3, 15.8, box, creamD, 33, 1, 6.6);
    oat(0, OF - 3, 17.6, box, cream, 30, 2.6, 5.6);
    const ogold = new THREE.MeshLambertMaterial({ color: 0xe0b040, emissive: 0x4a3200 });
    [-1, 1].forEach((sd) => {
      oat(sd * 17.5, OF - 3, 9.5, box, cream, 5, 19, 7);
      oat(sd * 17.5, OF - 3, 20.5, new THREE.SphereGeometry(2.4, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), copper, 1, 1.3, 1);
      oat(sd * 13.2, OF - 1, 21, box, ogold, 3.2, 4.6, 2);
      oat(sd * 13.2, OF - 1, 24, new THREE.SphereGeometry(1.2, 8, 6), ogold);
      oat(sd * 21, -4, 7, new THREE.CylinderGeometry(6, 6, 14, 16), cream);
      oat(sd * 21, -4, 14, new THREE.SphereGeometry(6.2, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2), copper, 1, 0.55, 1);
    });
    oat(0, 6, 18, new THREE.CylinderGeometry(11, 11.5, 2, 24), creamD);
    oat(0, 6, 19, new THREE.SphereGeometry(11, 24, 10, 0, Math.PI * 2, 0, Math.PI / 2), copper, 1, 0.42, 1);
    oat(0, 6, 23.6, new THREE.CylinderGeometry(1.6, 2.2, 1.6, 12), ogold);
    oat(0, -10, 15, box, cream, 28, 30, 12);
    oat(0, -10, 30, prismGeo(29, 7, 12.6), copper);
    oat(0, -3.8, 30.5, prismGeo(28, 6, 0.6), creamD);
    oat(0, -3.6, 39, new THREE.CylinderGeometry(0.7, 1, 4, 8), ogold);
    oat(0, -3.6, 41.5, box, ogold, 4, 0.6, 0.6);
    [-1, 1].forEach((sd) => oat(sd * 13, -3.6, 31.5, box, ogold, 1.6, 3, 1.2));
    // Площадь Оперы: перекрёсток авеню Оперы и Больших бульваров, у фасада — тротуар.
    const os = loc(o, ory, 0, OF + 6); add(box, lambert(0xd8cfba), os.x, oy + 1.35, os.z, 40, 0.3, 6, ory);
    const op = loc(o, ory, 0, OF + 22); add(box, lambert(0x5a5c62), op.x, oy + 1.3, op.z, 44, 0.3, 28, ory);
    // Дом инвалидов, как на аэрофото: сетка корпусов под серыми шиферными
    // крышами вокруг дворов, большой Почётный двор в центре; на юге —
    // Собор с золотым куполом на барабане с колоннами, фонарём и шпилем;
    // по сторонам собора регулярные сады; на север — эспланада к Сене.
    // Локально: v — на север (к эспланаде), u — поперёк.
    const iv = P.invalides, iy = height(iv.x, iv.z), iry = Math.atan2(P.grandpalais.x - iv.x, P.grandpalais.z - iv.z);
    const ivStone = facade(0xe2d8c0), slate = lambert(0x5c6670);
    const iwing = (u1, v1, u2, v2, h = 10) => {
      const a = loc(iv, iry, u1, v1), b = loc(iv, iry, u2, v2), len2 = Math.hypot(b.x - a.x, b.z - a.z), ang = Math.atan2(b.x - a.x, b.z - a.z);
      addHouse(ivStone, { x: (a.x + b.x) / 2, y: iy + h / 2, z: (a.z + b.z) / 2, sx: 6, sy: h, sz: len2 + 6, ry: ang });
      add(prismGeo(6.4, 4, len2 + 6), slate, (a.x + b.x) / 2, iy + h, (a.z + b.z) / 2, 1, 1, 1, ang);
    };
    // Внешний прямоугольник и внутренние корпуса: Почётный двор + по два двора с боков.
    iwing(-46, 36, 46, 36, 12);                              // северный фасад
    iwing(-46, -10, -46, 36); iwing(46, -10, 46, 36);
    iwing(-46, -10, 46, -10);
    iwing(-16, -10, -16, 36); iwing(16, -10, 16, 36);        // Почётный двор
    iwing(-31, -10, -31, 36); iwing(31, -10, 31, 36);
    iwing(-46, 13, -16, 13); iwing(16, 13, 46, 13);
    // Купол фасада по центру северного корпуса и церковь Святого Людовика.
    const ic = loc(iv, iry, 0, 36); add(box, ivStone, ic.x, iy + 8, ic.z, 12, 16, 8, iry);
    const sl = loc(iv, iry, 0, -16); add(box, lambert(0xe2d8c0), sl.x, iy + 8, sl.z, 14, 16, 12, iry);
    add(prismGeo(14.6, 5, 12.6), slate, sl.x, iy + 16, sl.z, 1, 1, 1, iry + Math.PI / 2);
    add(box, lambert(0xd8cfb8), loc(iv, iry, 0, 12).x, iy + 1.35, loc(iv, iry, 0, 12).z, 30, 0.3, 44, iry);
    // Собор с куполом.
    const ig = new THREE.MeshLambertMaterial({ color: 0xe8b84a, emissive: 0x5a3c00 }), ivCream = lambert(0xece2cc, { emissive: 0x2a2416 });
    const dc = loc(iv, iry, 0, -34);
    add(box, ivCream, dc.x, iy + 9, dc.z, 28, 18, 28, iry);
    const por = loc(iv, iry, 0, -49); add(box, ivCream, por.x, iy + 11, por.z, 14, 22, 4, iry);
    add(prismGeo(15, 4, 4.4), ivCream, por.x, iy + 22, por.z, 1, 1, 1, iry + Math.PI / 2);
    add(new THREE.CylinderGeometry(11, 11.5, 6, 24), ivCream, dc.x, iy + 21, dc.z);
    add(new THREE.CylinderGeometry(9, 9, 10, 24), ivCream, dc.x, iy + 29, dc.z);
    const dcols = [];
    for (let k = 0; k < 24; k++) { const a = k / 24 * Math.PI * 2; dcols.push({ x: dc.x + Math.cos(a) * 9.6, y: iy + 29, z: dc.z + Math.sin(a) * 9.6, sx: 0.55, sy: 10, sz: 0.55 }); }
    instanced(new THREE.CylinderGeometry(1, 1, 1, 6), lambert(0xf2ead8), dcols);
    add(new THREE.CylinderGeometry(9.6, 9.6, 1.2, 24), lambert(0xd8cdb4), dc.x, iy + 34.6, dc.z);
    add(new THREE.SphereGeometry(8.8, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2), ig, dc.x, iy + 35, dc.z, 1, 1.35, 1);
    add(new THREE.CylinderGeometry(1.8, 2.2, 5, 10), ig, dc.x, iy + 49, dc.z);
    add(new THREE.ConeGeometry(1.4, 9, 10), ig, dc.x, iy + 56, dc.z);
    add(box, ig, dc.x, iy + 61, dc.z, 0.3, 2, 0.3);
    // Сады у собора и эспланада на север.
    [-1, 1].forEach((sd) => { const g = loc(iv, iry, sd * 34, -36); add(box, lambert(0x7fae5a), g.x, iy + 1.35, g.z, 30, 0.3, 30, iry); add(new THREE.CylinderGeometry(6, 6, 0.4, 16), lambert(0x6a9a48), g.x, iy + 1.5, g.z); });
    const esp = loc(iv, iry, 0, 95);
    add(box, lambert(0x8fb065), esp.x, 1.3, esp.z, 60, 0.3, 100, iry);
    for (let v = 46; v < 145; v += 7) [-28, -20, 20, 28].forEach((u) => { const p = loc(iv, iry, u, v); tree(p.x, p.z); });
    // Пантеон: портик и купол на барабане с колоннадой.
    const pn = P.pantheon, py = height(pn.x, pn.z);
    add(box, lambert(0xe6dcc6), pn.x, py + 12, pn.z, 30, 24, 46);
    add(new THREE.CylinderGeometry(9, 9, 12, 20), lambert(0xeee6d4), pn.x, py + 30, pn.z);
    const pc = [];
    for (let k = 0; k < 20; k++) { const a = k / 20 * Math.PI * 2; pc.push({ x: pn.x + Math.cos(a) * 9.8, y: py + 30, z: pn.z + Math.sin(a) * 9.8, sx: 0.6, sy: 12, sz: 0.6 }); }
    instanced(new THREE.CylinderGeometry(1, 1, 1, 6), lambert(0xf2ecdf), pc);
    add(new THREE.SphereGeometry(8, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2), lambert(0xb8c0c4), pn.x, py + 36, pn.z, 1, 1.2, 1);
    add(new THREE.CylinderGeometry(1, 1.4, 5, 8), lambert(0xeee6d4), pn.x, py + 47, pn.z);
    // Помпиду: каркас с цветными трубами.
    const pd = P.pompidou, pdy = height(pd.x, pd.z);
    add(box, lambert(0xdde4ea), pd.x, pdy + 10, pd.z, 22, 20, 46);
    [0x2d6fd0, 0xd8352a, 0x3fae5a, 0xf2c230].forEach((c, k) => { for (let v = -20; v <= 20; v += 8) add(new THREE.CylinderGeometry(0.9, 0.9, 20, 8), lambert(c), pd.x - 11.6 + k * 0.1, pdy + 10, pd.z + v + k * 1.8); });
    const esc = add(box, lambert(0xd8352a), pd.x + 11.6, pdy + 11, pd.z, 1.4, 1.4, 44); esc.rotation.x = 0.4;
    // Бастилия: колонна с золотым Гением свободы.
    const b = P.bastille, by = height(b.x, b.z);
    add(new THREE.CylinderGeometry(22, 22, 0.5, 28), lambert(0x9a958c), b.x, by + 1.3, b.z);
    add(box, lambert(0x8a8478), b.x, by + 4, b.z, 7, 6, 7);
    add(new THREE.CylinderGeometry(1.8, 2, 34, 12), lambert(0x4f5a50), b.x, by + 24, b.z);
    add(new THREE.SphereGeometry(1.4, 8, 6), gold, b.x, by + 42, b.z);
    add(new THREE.ConeGeometry(0.8, 3.4, 6), gold, b.x, by + 44.5, b.z);
    // Монпарнас: тёмная одинокая башня.
    const mt = P.montparnasse;
    add(box, new THREE.MeshPhongMaterial({ color: 0x2f3438, specular: 0x8899aa, shininess: 60 }), mt.x, height(mt.x, mt.z) + 55, mt.z, 22, 110, 14, 0.4);
    add(box, lambert(0x22272b), mt.x, height(mt.x, mt.z) + 111, mt.z, 22.5, 2, 14.5, 0.4);
    redLights.push(add(new THREE.SphereGeometry(0.8, 8, 6), lightMat(0x8a2a22, 0xff3b30), mt.x, height(mt.x, mt.z) + 113, mt.z));
    // Люксембургский сад: дворец, газон, бассейн, деревья.
    const lx = P.luxembourg, ly = height(lx.x, lx.z);
    add(box, lambert(0x8fb065), lx.x, ly + 0.4, lx.z, 90, 0.3, 110);
    add(new THREE.CylinderGeometry(9, 9, 0.6, 20), new THREE.MeshPhongMaterial({ color: 0x6fb8d8, shininess: 80 }), lx.x, ly + 0.7, lx.z);
    add(box, lambert(0xe2d8c2), lx.x, ly + 8, lx.z + 44, 46, 14, 14);
    add(box, lambert(0x6f7a86), lx.x, ly + 17, lx.z + 44, 42, 4, 11);
    for (let k = 0; k < 160; k++) { const px = lx.x + rand(-45, 45), pz = lx.z + rand(-54, 34); if (Math.hypot(px - lx.x, pz - lx.z) > 22) tree(px, pz); }
  }

  yield 0.64;
  // ─── Дефанс: башни и Большая арка ────────────────────────────────────────
  // По фото: башни разной формы — прямые, скруглённые, ступенчатые, со
  // скошенным верхом — по обе стороны широкой эспланады; в конце оси
  // Большая арка (полый белый куб), рядом низкий белый свод CNIT. Ни одна
  // башня не стоит на эспланаде и в Сене (проверка всего пятна).
  {
    const d = P.defense, dy = height(d.x, d.z), ry = Math.atan2(P.arc.x - d.x, P.arc.z - d.z);
    const white = lambert(0xf2f2ee, { emissive: 0x3a3a38 }), dHole = lambert(0x3a4048, { side: THREE.DoubleSide });
    const dat = (u, v, yy, geom, mat, sx = 1, sy = 1, sz = 1, rot = 0) => { const p = loc(d, ry, u, v); return add(geom, mat, p.x, height(p.x, p.z) + yy, p.z, sx, sy, sz, ry + rot); };
    // Большая арка: рама 44 × 44, глубина 34, тонкие сетки-«облака» внутри.
    const S = 44, T = 6;
    dat(0, 0, S - T / 2, box, white, S, T, 34); dat(0, 0, T / 2, box, white, S, T, 34);
    [-1, 1].forEach((sd) => dat(sd * (S / 2 - T / 2), 0, S / 2, box, white, T, S, 34));
    dat(0, 0, S / 2, box, dHole, S - 2 * T, S - 2 * T, 0.2);
    // Ступени-парвис перед аркой и эспланада к Парижу.
    for (let k = 0; k < 5; k++) dat(0, 20 + k * 4, 0.5 + (4 - k) * 0.7, box, lambert(0xd8d4cc), 70, 1.4, 4);
    const ep = loc(d, ry, 0, 120); add(box, lambert(0xd8d4cc), ep.x, height(ep.x, ep.z) + 1.3, ep.z, 50, 0.3, 190, ry);
    // CNIT: низкий белый треугольный свод к северу от эспланады.
    const free = (x, z, r) => {
      for (let k = 0; k < 8; k++) { const a = k / 8 * Math.PI * 2, px = x + Math.cos(a) * r, pz = z + Math.sin(a) * r; if (inRiver(px, pz, 6) || rawHeight(px, pz) < 1) return false; }
      return !inRiver(x, z, 6);
    };
    BLOCKERS.push({ x: d.x, z: d.z, r: 36 });
    // CNIT — первое свободное место на суше к северу от эспланады.
    for (const [cu, cv] of [[-62, 10], [-62, -20], [-62, 40], [-90, 10]]) {
      const c = loc(d, ry, cu, cv);
      if (!free(c.x, c.z, 30)) continue;
      add(new THREE.SphereGeometry(28, 3, 4, 0, Math.PI * 2, 0, Math.PI / 2), white, c.x, height(c.x, c.z), c.z, 1, 0.5, 1, ry + Math.PI / 6);
      BLOCKERS.push({ x: c.x, z: c.z, r: 30 });
      break;
    }
    // Башни: тип, пятно, высота. Ставим по сторонам эспланады (|u| > 34).
    const glass = [facade(0x6f9ec2), facade(0x5d8fb8), facade(0x8fb4c8), facade(0x4a6a80), facade(0x9fb8c4)];
    const dark = new THREE.MeshPhongMaterial({ color: 0x3a4a5a, specular: 0xffffff, shininess: 90, flatShading: true });
    let placed = 0;
    for (let k = 0; k < 120 && placed < 22; k++) {
      const su = rnd() < 0.5 ? -1 : 1, u = su * rand(36, 120), v = rand(-70, 55);   // только на берегу Дефанс
      const p = loc(d, ry, u, v), w = rand(14, 20), dd = rand(12, 18);
      if (!free(p.x, p.z, Math.hypot(w, dd) / 2 + 2)) continue;
      const y = height(p.x, p.z), h = rand(55, 125), kind = k % 4, ang = ry + rand(-0.25, 0.25);
      if (kind === 0) addHouse(pick(glass), { x: p.x, y: y + h / 2, z: p.z, sx: w, sy: h, sz: dd, ry: ang });
      if (kind === 1) {                                           // скруглённая («First»)
        add(new THREE.CylinderGeometry(w / 2, w / 2, h, 10), dark, p.x, y + h / 2, p.z, 1, 1, dd / w, ang);
      }
      if (kind === 2) {                                           // ступенчатая («Majunga»)
        addHouse(pick(glass), { x: p.x, y: y + h * 0.35, z: p.z, sx: w, sy: h * 0.7, sz: dd, ry: ang });
        addHouse(pick(glass), { x: p.x, y: y + h * 0.85, z: p.z, sx: w * 0.65, sy: h * 0.3, sz: dd * 0.7, ry: ang });
      }
      if (kind === 3) {                                           // скошенный верх
        addHouse(pick(glass), { x: p.x, y: y + h * 0.45, z: p.z, sx: w, sy: h * 0.9, sz: dd, ry: ang });
        add(prismGeo(w, h * 0.18, dd), dark, p.x, y + h * 0.9, p.z, 1, 1, 1, ang);
      }
      if (h > 110) redLights.push(add(new THREE.SphereGeometry(0.8, 8, 6), lightMat(0x8a2a22, 0xff3b30), p.x, y + h + 1, p.z));
      BLOCKERS.push({ x: p.x, z: p.z, r: Math.hypot(w, dd) / 2 + 3 });
      placed++;
    }
  }

  yield 0.68;
  // ─── Булонский и Венсенский леса ─────────────────────────────────────────
  {
    for (let k = 0; k < 3200; k++) {
      const x = rand(-ISLAND, ISLAND), z = rand(-ISLAND, ISLAND);
      if (!inWood(x, z) && !(Math.hypot(x, z) > ISLAND * 0.82 && Math.hypot(x, z) < ISLAND * 0.95 && rnd() < 0.5)) continue;
      if (inRiver(x, z, 4)) continue;
      tree(x, z);
    }
    [[P.boulogne, 0, -40, 22, 60], [P.vincennes, 30, 0, 40, 22]].forEach(([c, du, dv, rx, rz]: any) => {
      const l = add(new THREE.CircleGeometry(1, 24), new THREE.MeshPhongMaterial({ color: 0x5f8fa0, shininess: 70 }), c.x + du, 1.5, c.z + dv, rx, rz, 1);
      l.rotation.x = -Math.PI / 2;
    });
  }

  yield 0.86;
  blocks.forEach((list, mat) => instanced(ringGeo, mat, list));
  instanced(mansardGeo, lambert(0x7d8894), mansards);
  houses.forEach((list, mat) => instanced(box, mat, list));
  instanced(new THREE.ConeGeometry(0.75, 1, 4), lambert(0x6f7a86), roofsTile);
  const DETAIL_SECTOR = 400, DETAIL_RANGE = 800;
  const detail = distanceCuller(DETAIL_RANGE);
  const nearOnly = (geom, material, list) => { if (list.length) detail.add(instancedBySector(scene, geom, material, list, DETAIL_SECTOR)); };
  // Деревья и фонари, попавшие в пятно постройки, убираем (ствол и крона — парами).
  const blocked = (it) => BLOCKERS.some((b) => Math.hypot(it.x - b.x, it.z - b.z) < b.r);
  for (let i = trunks.length - 1; i >= 0; i--) if (blocked(trunks[i])) { trunks.splice(i, 1); crowns.splice(i, 1); }
  for (let i = lampPoles.length - 1; i >= 0; i--) if (blocked(lampPoles[i])) { lampPoles.splice(i, 1); lampHeads.splice(i, 1); }
  nearOnly(box, lambert(0x7a5f45), trunks);
  nearOnly(new THREE.IcosahedronGeometry(1, 0), lambert(0x6a9a48), crowns);
  nearOnly(box, lambert(0x2f3a36), lampPoles);
  nearOnly(box, lightMat(0x8a8474, 0xffd98a), lampHeads);

  yield 0.90;
  // ─── Звёзды для сбора ────────────────────────────────────────────────────
  const starShape = new THREE.Shape();
  for (let i = 0; i < 10; i++) {
    const a = i / 10 * Math.PI * 2 - Math.PI / 2, r = i % 2 ? 0.45 : 1;
    i ? starShape.lineTo(Math.cos(a) * r, Math.sin(a) * r) : starShape.moveTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  const starGeo = new THREE.ExtrudeGeometry(starShape, { depth: 0.35, bevelEnabled: false }); starGeo.center();
  const starMat = new THREE.MeshLambertMaterial({ color: 0xffd23f, emissive: 0x8a5a00 });
  const pickups = [];
  function star(x, y, z) { const s = add(starGeo, starMat, x, y, z, 2.4, 2.4, 2.4); pickups.push(s); }
  {
    // Спираль вокруг Эйфелевой башни.
    const e = P.eiffel;
    for (let i = 0; i < 16; i++) { const a = i * 0.85; star(e.x + Math.cos(a) * 36, 15 + i * 9, e.z + Math.sin(a) * 36); }
    // Сквозь Триумфальную арку и над Елисейскими полями.
    star(P.arc.x, 18, P.arc.z);
    for (let t = 0.12; t < 1; t += 0.12) star(P.arc.x + (P.concorde.x - P.arc.x) * t, 26, P.arc.z + (P.concorde.z - P.arc.z) * t);
    // Над Сеной — цепочка от Нотр-Дама до башни.
    for (let t = 0.2; t < 0.5; t += 0.03) { const p = seinePoint(t); star(p.x, 14, p.z); }
    // Кольца: вокруг Сакре-Кёр, Дома инвалидов, колеса и над Лувром.
    [[P.sacre, 30, 80], [P.invalides, 34, 60], [P.louvre, 26, 30]].forEach(([c, r, y]: any) => { for (let i = 0; i < 8; i++) { const a = i / 8 * Math.PI * 2; star(c.x + Math.cos(a) * r, height(c.x, c.z) + y, c.z + Math.sin(a) * r); } });
    [['notredame', 44], ['opera', 40], ['pantheon', 58], ['pompidou', 30], ['bastille', 52], ['montparnasse', 124], ['moulin', 30], ['defense', 50]]
      .forEach(([k, y]: any) => star(P[k].x, height(P[k].x, P[k].z) + y, P[k].z));
    [['eiffel', 'trocadero'], ['louvre', 'notredame'], ['opera', 'sacre'], ['notredame', 'pantheon'], ['arc', 'defense'], ['invalides', 'eiffel']].forEach(([a, b]) => {
      const A = P[a], B = P[b];
      for (let t = 0.15; t < 0.9; t += 0.15) { const px = A.x + (B.x - A.x) * t, pz = A.z + (B.z - A.z) * t; star(px, height(px, pz) + 35 + Math.sin(t * Math.PI) * 18, pz); }
    });
  }

  yield 0.95;
  // ─── Время суток ─────────────────────────────────────────────────────────
  const K = [
    { t: 0.0, top: 0x4a86c8, mid: 0xa4c8e6, horizon: 0xe2ecf4, fog: 0xccdcea, sun: 0xfff2dc, sunI: 1.65, hemi: 1.5, sunAlt: 0.55 },
    { t: 0.5, top: 0x2e3a6e, mid: 0xb87090, horizon: 0xffa868, fog: 0xd9a08a, sun: 0xffa05a, sunI: 1.5, hemi: 1.1, sunAlt: 0.04 },
    { t: 1.0, top: 0x05081a, mid: 0x0f1838, horizon: 0x2a2748, fog: 0x161b33, sun: 0x8fa6ff, sunI: 0.55, hemi: 0.8, sunAlt: -0.25 },
  ];
  const tmpA = new THREE.Color(), tmpB = new THREE.Color();
  function lerpHex(a, b, k) { return tmpA.setHex(a).lerp(tmpB.setHex(b), k).clone(); }
  let glowNow = 0;
  function setTime(t) {
    const i = t < 0.5 ? 0 : 1, a = K[i], b = K[i + 1], k = (t - a.t) / (b.t - a.t);
    skyUniforms.top.value.copy(lerpHex(a.top, b.top, k));
    skyUniforms.mid.value.copy(lerpHex(a.mid, b.mid, k));
    skyUniforms.horizon.value.copy(lerpHex(a.horizon, b.horizon, k));
    fog.color.copy(lerpHex(a.fog, b.fog, k));
    const sunColor = lerpHex(a.sun, b.sun, k);
    const alt = THREE.MathUtils.lerp(a.sunAlt, b.sunAlt, k);
    // Закат — на западе, за Дефанс.
    const sunDir = new THREE.Vector3(0.3 + 0.7 * smooth(0, 0.5, t), Math.max(alt, -0.2), -0.9 + 0.8 * smooth(0, 0.5, t)).normalize();
    sun.position.copy(sunDir).multiplyScalar(1000);
    sun.color.copy(sunColor); sun.intensity = THREE.MathUtils.lerp(a.sunI, b.sunI, k);
    hemi.intensity = THREE.MathUtils.lerp(a.hemi, b.hemi, k);
    hemi.color.copy(lerpHex(0xffffff, 0x9fb0ff, smooth(0.5, 1, t)));
    skyUniforms.sunDir.value.copy(sunDir);
    skyUniforms.sunColor.value.copy(sunColor);
    skyUniforms.sunStrength.value = 1 - smooth(0.62, 0.8, t);
    const glow = smooth(0.35, 0.75, t);
    glowNow = glow;
    lights.forEach((l) => l.m.color.copy(l.day).lerp(l.night, glow));
    facadeGlow.value = glow;
    eiffelMat.emissiveIntensity = glow * 0.85;
    nightStars.material.opacity = smooth(0.65, 0.95, t);
    fog.near = THREE.MathUtils.lerp(FOG_NEAR_DAY, FOG_NEAR_NIGHT, smooth(0.5, 1, t));
  }
  function updateAnim(dt, elapsed) {
    if (wheel.mesh) wheel.mesh.rotation.z = elapsed * 0.08;
    if (millBlades.mesh) millBlades.mesh.rotation.z = -elapsed * 0.6;
    boats.forEach((b) => {
      b.t = (b.t + b.dir * dt * 0.004 + 1) % 1;
      const p = seinePoint(b.t), nx = Math.cos(p.ang), nz = -Math.sin(p.ang);
      b.g.position.set(p.x + nx * b.side, 0.4, p.z + nz * b.side);
      b.g.rotation.y = p.ang + (b.dir > 0 ? 0 : Math.PI);
    });
    // Мерцание башни: первые 5 минут каждого часа в Париже, у нас — всю ночь.
    sparkles.visible = glowNow > 0.6;
    sparkles.material.opacity = 0.35 + 0.65 * Math.abs(Math.sin(elapsed * 23) * Math.sin(elapsed * 7.3));
  }

  yield 0.97;
  const animated = [sky, nightStars, sparkles, ...pickups, ...redLights, ...boats.map((b) => b.g)];
  if (wheel.mesh) animated.push(wheel.mesh);
  mergeStaticMeshes(scene, animated, lights.map((l) => l.m), SECTOR_SIZE);

  setTime(0);
  updateAnim(0, 0);
  // Старт за Трокадеро, курс на Эйфелеву башню и Марсово поле.
  const back = { x: P.trocadero.x - (P.eiffel.x - P.trocadero.x) * 0.9, z: P.trocadero.z - (P.eiffel.z - P.trocadero.z) * 0.9 };
  const spawnYaw = Math.atan2(-(P.eiffel.x - back.x), -(P.eiffel.z - back.z));
  return {
    height,
    pickups,
    spawn: { x: back.x, y: 60, z: back.z, yaw: spawnYaw },
    radius: ISLAND * 0.92,
    maxY: 260,
    update(dt, elapsed, timeOfDay, cameraPosition) {
      setTime(timeOfDay);
      pickups.forEach((s) => { s.rotation.y = elapsed * 2; });
      updateAnim(dt, elapsed);
      redLights.forEach((l) => { l.visible = timeOfDay < 0.35 || Math.sin(elapsed * 3) > -0.3; });
      detail.update(cameraPosition);
      sky.position.copy(cameraPosition);
      nightStars.position.copy(cameraPosition);
    },
  };
}
